import _t from 'i18n!nls/enterprise-admin-messaging-v2';

/**
 * Identifier for each pre-canned AI prompt the admin can pick from. Used
 * both as the key into `AI_MESSAGE_ACTIONS` and as the
 * `?messageAction=<KEY>` URL param value used by external entry points.
 *
 * **Order matches `AI_MESSAGE_ACTIONS` below** (which mirrors the spec at
 * `zz-messaging-specs/prompt_suggestions.txt`). That order is load-bearing
 * for the rotating suggestion chips on the Define-goal screen — see
 * `ALL_AI_MESSAGE_ACTION_KEYS`.
 *
 * Keys marked `(public API)` are referenced by name from external bundles
 * (`enterprise-admin-home`, `enterprise-admin-recommended-actions`,
 * `enterprise-admin-messages`) and must not be renamed without a
 * coordinated change across those bundles.
 */
export type AIMessageActionKey =
  | 'REMIND_TO_JOIN' //               (public API)
  | 'MESSAGE_INACTIVE_LEARNERS' //    (public API)
  | 'ENCOURAGE_NEW_ENROLLEES'
  | 'REMIND_TO_ENROLL'
  | 'MESSAGE_ENROLLED_LEARNERS' //    (public API)
  | 'SEND_ACCOLADES' //               (public API)
  | 'REMINDER_TO_COMPLETE_COURSE' //  (public API)
  | 'COURSE_DEADLINE_REMINDER'
  | 'REENGAGE_LONG_INACTIVE'
  | 'PROMOTE_POPULAR_COURSES'
  | 'CAREER_OPPORTUNITIES'
  | 'WELCOME_NEW_JOINERS'
  | 'ENCOURAGE_ONGOING_LEARNING'
  | 'REMIND_UPSKILLING_BENEFITS';

export type AIMessageAction = {
  /**
   * Translated prompt text. Implemented as a getter so `_t()` is evaluated
   * at access time (locale changes propagate without restart).
   */
  readonly prompt: string;
};

/**
 * Centralised registry of the AI-generated message prompts surfaced across
 * the admin app. Adding a new entry here makes it linkable as
 * `?messageAction=<KEY>` from any page (once the URL listener ships) and
 * appends it to the end of the rotating Define-goal suggestion chips.
 *
 * **Insertion order matches `zz-messaging-specs/prompt_suggestions.txt`
 * and is the rotation order for the Define-goal chips** (see
 * `ALL_AI_MESSAGE_ACTION_KEYS`). Reordering properties here changes which
 * chips admins see first.
 */
export const AI_MESSAGE_ACTIONS: Record<AIMessageActionKey, AIMessageAction> = {
  REMIND_TO_JOIN: {
    get prompt() {
      return _t('Remind invited learners in the last 30 days to join Coursera');
    },
  },
  MESSAGE_INACTIVE_LEARNERS: {
    get prompt() {
      return _t('Remind inactive learners in the last 15 days to come back to Coursera');
    },
  },
  ENCOURAGE_NEW_ENROLLEES: {
    get prompt() {
      return _t('Encourage learners who enrolled in the last 30 days to get started');
    },
  },
  REMIND_TO_ENROLL: {
    get prompt() {
      return _t('Remind learners to enroll in a new course or Specialization');
    },
  },
  MESSAGE_ENROLLED_LEARNERS: {
    get prompt() {
      return _t('Encourage enrolled learners to continue making progress');
    },
  },
  SEND_ACCOLADES: {
    get prompt() {
      return _t('Congratulate learners who have completed a course in the last 30 days');
    },
  },
  REMINDER_TO_COMPLETE_COURSE: {
    get prompt() {
      return _t('Remind learners to finish a course to complete it');
    },
  },
  COURSE_DEADLINE_REMINDER: {
    get prompt() {
      return _t('Notify members about an upcoming course deadline');
    },
  },
  REENGAGE_LONG_INACTIVE: {
    get prompt() {
      return _t('Re-engage learners inactive for the last 30 days');
    },
  },
  PROMOTE_POPULAR_COURSES: {
    get prompt() {
      return _t('Share top trending courses within your organization');
    },
  },
  CAREER_OPPORTUNITIES: {
    get prompt() {
      return _t('Motivate learners to upskill or advance their careers');
    },
  },
  WELCOME_NEW_JOINERS: {
    get prompt() {
      return _t('Welcome new joiners in the last 30 days to Coursera and encourage them to get started');
    },
  },
  ENCOURAGE_ONGOING_LEARNING: {
    get prompt() {
      return _t('Promote the benefits of continuous learning');
    },
  },
  REMIND_UPSKILLING_BENEFITS: {
    get prompt() {
      return _t('Highlight the value of upskilling in today’s workplace');
    },
  },
};

/**
 * All registered action keys, **in insertion order**. This is the rotation
 * sequence for the Define-goal suggestion chips: offset 0 → first
 * `SUGGESTION_COUNT` entries, then it advances by `SUGGESTION_COUNT` on
 * every Generate click and wraps. Reordering properties in
 * `AI_MESSAGE_ACTIONS` changes which chips admins see first.
 */
export const ALL_AI_MESSAGE_ACTION_KEYS = Object.keys(AI_MESSAGE_ACTIONS) as AIMessageActionKey[];
